import { Box, Container, Typography, Stack, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import BusinessIcon from '@mui/icons-material/Business';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import VerifiedIcon from '@mui/icons-material/Verified';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const GlassCard = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 48px rgba(0, 0, 0, 0.4)'
      : '0 12px 48px rgba(0, 0, 0, 0.15)',
  },
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingLeft: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '7px',
    top: '24px',
    bottom: '-16px',
    width: '2px',
    background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  },
  '&:last-child::before': {
    display: 'none',
  },
}));

const TimelineDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: '16px',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  background: theme.palette.primary.main,
  border: `3px solid ${theme.palette.background.default}`,
  boxShadow: `0 0 0 4px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
}));

const StatCard = styled(GlassCard)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
}));

export default function CompanyProfile() {
  const { t } = useLanguage();

  const values = [
    {
      icon: <EmojiObjectsIcon sx={{ fontSize: 48 }} />,
      title: t('company.values.innovation'),
      desc: t('company.values.innovation.desc'),
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: 48 }} />,
      title: t('company.values.quality'),
      desc: t('company.values.quality.desc'),
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 48 }} />,
      title: t('company.values.trust'),
      desc: t('company.values.trust.desc'),
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 48 }} />,
      title: t('company.values.teamwork'),
      desc: t('company.values.teamwork.desc'),
    },
  ];

  const timeline = [
    { year: '2010', title: t('company.history.2010'), desc: t('company.history.2010.desc') },
    { year: '2015', title: t('company.history.2015'), desc: t('company.history.2015.desc') },
    { year: '2020', title: t('company.history.2020'), desc: t('company.history.2020.desc') },
    { year: '2025', title: t('company.history.2025'), desc: t('company.history.2025.desc') },
  ];

  const certifications = [
    t('company.certifications.iso'),
    t('company.certifications.iso27001'),
    t('company.certifications.microsoft'),
    t('company.certifications.aws'),
  ];

  const stats = [
    { value: t('why.experience.value'), label: t('why.experience.title') },
    { value: t('why.projects.value'), label: t('why.projects.title') },
    { value: t('why.clients.value'), label: t('why.clients.title') },
    { value: t('why.team.value'), label: t('why.team.title') },
  ];

  return (
    <>
      <SEOHead titleKey="company.title" descriptionKey="company.intro.desc" />
      <Box component="main" role="main">
        {/* Hero Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            pt: { xs: 16, md: 20 },
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('company.intro.title')}
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: '800px', lineHeight: 1.8, fontSize: '1.25rem' }}
              >
                {t('company.intro.desc')}
              </Typography>
            </Stack>
          </Container>
        </PageSection>

        {/* Statistics Section */}
        <PageSection sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <Grid container spacing={3}>
              {stats.map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <StatCard>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: '2.5rem', md: '3rem' },
                        fontWeight: 700,
                        background: (theme) =>
                          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '1rem' }}>
                      {stat.label}
                    </Typography>
                  </StatCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </PageSection>

        {/* History Timeline */}
        <PageSection>
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('company.history.title')}
                </Typography>
              </Stack>
              <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                {timeline.map((item, index) => (
                  <TimelineItem key={index}>
                    <TimelineDot />
                    <GlassCard>
                      <Typography
                        variant="h6"
                        color="primary"
                        fontWeight={700}
                        gutterBottom
                        sx={{ fontSize: '1.1rem' }}
                      >
                        {item.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                        {item.desc}
                      </Typography>
                    </GlassCard>
                  </TimelineItem>
                ))}
              </Box>
            </Stack>
          </Container>
        </PageSection>

        {/* Values Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('company.values.title')}
                </Typography>
              </Stack>
              <Grid container spacing={4}>
                {values.map((value, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <GlassCard sx={{ height: '100%', textAlign: 'center' }}>
                      <Stack spacing={2} alignItems="center">
                        <Box sx={{ color: 'primary.main' }}>{value.icon}</Box>
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                          {value.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                          {value.desc}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>

        {/* Certifications Section */}
        <PageSection>
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('company.certifications.title')}
                </Typography>
              </Stack>
              <Grid container spacing={3}>
                {certifications.map((cert, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <GlassCard>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <WorkspacePremiumIcon
                          sx={{ fontSize: 40, color: 'primary.main' }}
                        />
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                          {cert}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>
      </Box>
    </>
  );
}